import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Field as FormikField, Formik, Form as FormikForm } from 'formik';

import { useSentry } from '../sentry';
import { usePageMeta } from '../page';

// Utils
import { consistentString } from '../../utils/content-formatting';
import {
  getInitialValues,
  getValidationSchema,
  inferConsentTypes,
  buildFieldsFromContentfulData,
  determinePostSubmissionJourney,
  generateSteps,
  sanitiseValues,
  shouldReveal,
} from './helpers';
import submit from './submit';
import { sendEvent } from '../../utils/analytics';
import { isBrowser } from '../../utils/is-browser';
import { useDeviceType } from '../../utils/window-size';
import { useLayout } from '../layout/context';

// Components
import { Container } from '../styled/containers';
import Button from '../button';
import Fieldset, { FieldsetPropTypes } from '../form-elements/fieldset';
import InlineCallout from '../inline-callout';
import RichText from '../rich-text';
import Field, { FieldPropTypes } from '../form-elements/field';
import Address from '../form-elements/address';
import Postcode from '../form-elements/address/postcode';
import DateOfBirth from '../form-elements/date-of-birth';
import SubmitErrorHandler from './submitErrorHandler';
import FieldMessage from '../form-elements/field-message';
import FileUpload from '../form-elements/file-upload';
import { VisuallyHidden } from '../styled/accessibility';

// PropTypes
import { RichTextPropTypes } from '../../prop-types';

// Styles
import {
  ModuleWrapper,
  FormWrapper,
  FormFieldWrapper,
  PreviousButtonWrapper,
} from './styles';
import Consent from '../form-elements/consent';

// Constants
import fieldTypes from './field-types';

export const renderFields = (fields, refFn, values) =>
  fields.map((field, i) => {
    const fieldKey = `${field.name}-${i}`;
    if (field.richText) {
      const { text, ...rest } = field;
      return <RichText key={fieldKey} {...rest} richText={text} />;
    }

    if (field.fieldset) {
      return (
        <Fieldset
          key={fieldKey}
          legend={field.legend}
          description={field.description}
        >
          {field.fields && renderFields(field.fields, refFn)}
        </Fieldset>
      );
    }

    if (field.type === fieldTypes.ADDRESS) {
      return <Address key={fieldKey} ref={refFn} {...field} />;
    }

    if (field.type === fieldTypes.ADDRESS_INTERNATIONAL) {
      return <Address key={fieldKey} ref={refFn} {...field} international />;
    }

    if (field.type === fieldTypes.POSTCODE) {
      return <Postcode key={fieldKey} ref={refFn} {...field} />;
    }

    if (field.type === fieldTypes.DATE_OF_BIRTH) {
      return <DateOfBirth key={fieldKey} ref={refFn} {...field} />;
    }

    if (field.type === fieldTypes.FILE_UPLOAD) {
      return (
        <FileUpload
          key={fieldKey}
          name={field.machineName}
          ref={refFn}
          field={field}
        />
      );
    }

    if (field.revealValues && field.revealFields) {
      return (
        <>
          <Field key={fieldKey} ref={refFn} {...field} />
          {shouldReveal(values, field) &&
            renderFields(field.revealFields, refFn, values)}
        </>
      );
    }

    return <Field key={fieldKey} ref={refFn} {...field} />;
  });
const Form = ({
  formEntryId,
  adminTitle,
  campaignId,
  fields,
  submitCallToAction,
  thankYouMessage,
  redirectAfterSubmission,
  backgroundColor,
  formHeader,
  showConsent,
  captcha,
  event,
  submitColor = 'secondary-standard',
  consentReplacement = null,
  sendToCRM,
  isSurvey = false,
}) => {
  const {
    insideContainer,
    createdAt,
    updatedAt,
    lastAmended,
    title,
    pageContentfulId,
  } = useLayout();
  const { contentfulId, path, type } = usePageMeta();
  const [submitted, setSubmitted] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [updatedFormValues, setUpdatedFormValues] = useState();
  const [cachedValues, setCachedValues] = useState();
  const steps = useMemo(() => generateSteps(fields), [fields]);
  const allFields = steps.flat();
  const totalSteps = steps.length;
  const isSteppedForm = totalSteps > 1;
  const isFinalStep = currentStep === totalSteps - 1;
  const deviceType = useDeviceType();

  let cachedDataKey;
  let urlWithoutQueryParams;
  let surveyUserId;

  if (isBrowser) {
    [urlWithoutQueryParams] = window.location.href.split('?');
    cachedDataKey = `${formEntryId || 'form'}-${urlWithoutQueryParams}`;
    surveyUserId = window.CookieControl?.getCookie('survey_user_id');
  }

  const hiddenFields = {
    formEntryId,
    formName: consistentString(adminTitle),
    formHeader,
    spaceId: process.env.SHELTER_SITE,
    ctflEnv: process.env.CONTENTFUL_ENVIRONMENT,
    campaignId,
    sendToCRM,
    ...(isSurvey && {
      surveyUserId,
      pageContentfulId,
      pageUrl: urlWithoutQueryParams,
      pageCreatedAt: createdAt,
      pageUpdatedAt: updatedAt,
      pageLastAmended: lastAmended,
      pageTitle: title,
      deviceType,
    }),
  };

  const defaultInitialValues = {
    ...hiddenFields,
    ...(showConsent ? { consent: [] } : {}),
    ...getInitialValues(allFields),
  };

  // on initial load use cached values or default values
  // on each subsequent rerender use updated form values
  const formValues = updatedFormValues || cachedValues || defaultInitialValues;

  if (captcha) {
    formValues.captcha = true;
  }

  const formHeaderRef = useRef(null);
  const FormHeaderElement = isSurvey ? VisuallyHidden : 'h3';
  const formTop = useRef(null);
  const messageRef = useRef(null);
  const fieldRefs = useRef([]);
  fieldRefs.current = [];

  const addToFieldRefs = (el) => {
    if (el && el.checkboxes && el.checkboxes.length) {
      el.checkboxes.forEach((checkbox) => {
        if (!fieldRefs.current.includes(checkbox)) {
          fieldRefs.current.push(checkbox);
        }
      });
    }

    if (el && !fieldRefs.current.includes(el)) {
      fieldRefs.current.push(el);
    }
  };

  // On the initial component load, or after a page refresh, check local storage to see if form has already been started
  useEffect(() => {
    const cachedData = JSON.parse(localStorage.getItem(cachedDataKey));

    if (isSteppedForm && cachedData) {
      const { activeStep } = cachedData;

      // strip out cached file uploads here
      Object.keys(cachedData).forEach((key) => {
        if (cachedData[key]?.files) cachedData[key].files = [];
      });

      // update the form values passed to Formik on page load
      setCachedValues({
        ...defaultInitialValues,
        ...cachedData,
      });
      setCurrentStep(activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messageRef && messageRef.current && submitted) {
      window.scrollTo(0, messageRef.current.offsetTop);
    }
  }, [submitted]); // Only re-run the effect if submitted changes

  // auto scrolls user to the top of the form when the next/prev button clicked
  const scrollToTop = useCallback(() => {
    formTop.current?.scrollIntoView(true);

    // briefly makes the heading "Step x of y" tabbable in order to shift the focus of the form, then resets the tabIndex value to -1
    // this is a workaround for Safari/Firefox tab order not updating after scrollIntoView is called
    const stepHeadingElement = formHeaderRef?.current;
    stepHeadingElement.tabIndex = 0;
    stepHeadingElement?.focus();
    stepHeadingElement.tabIndex = -1;
  }, [formTop, formHeaderRef]);

  const validationSchema = () => getValidationSchema(steps[currentStep]);

  const onAPISuccess = useCallback(
    (values) => {
      if (isSteppedForm) localStorage.removeItem(cachedDataKey);
      if (!redirectAfterSubmission) {
        setSubmitted(true);
        return;
      }

      return determinePostSubmissionJourney(
        event,
        values,
        redirectAfterSubmission
      );
    },
    [event, redirectAfterSubmission, cachedDataKey, isSteppedForm]
  );

  const Sentry = useSentry();
  const onAPIFailure = useCallback(
    (err) => {
      if (err.response) {
        const { message } = err.response.data;
        setResponseMessage(message);

        const captchaError = err.response.data?.data?.error
          .toLowerCase()
          .includes('captcha')
          ? err.response.data.data.error
          : null;

        if (Sentry && captchaError) {
          Sentry.withScope((scope) => {
            scope.setExtra('Captcha error', captchaError);
            scope.setExtra('contentfulId', contentfulId);
            scope.setExtra('path', path);
            scope.setExtra('page-type', type);
            Sentry.captureMessage('Captcha Error');
            Sentry.captureException(err);
          });
        }
      } else {
        setResponseMessage(
          'Sorry, form submission failed for an unknown reason'
        );
      }
    },
    [Sentry]
  );

  // write form values to local storage and update form values (stepped forms only)
  const updateFormDataAndCache = useCallback(
    (values, stepIncrement) => {
      // step increment will be -1 on 'Previous' button click, and +1 on 'Next' button click
      const activeStep = currentStep + stepIncrement;
      const cachedData = {
        ...values,
        activeStep,
      };

      if (!isSurvey) {
        localStorage.setItem(cachedDataKey, JSON.stringify(cachedData));
      }
      setUpdatedFormValues(cachedData);
      setCurrentStep(currentStep + stepIncrement);
    },
    [cachedDataKey, currentStep, isSurvey]
  );

  /**
   * Formik submit function
   */
  const onSubmit = useCallback(
    async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      let token = null;

      if (currentStep < totalSteps - 1) {
        // update the form data and cached progress each time a step is completed
        updateFormDataAndCache(values, 1);
        return scrollToTop();
      }

      if (captcha) {
        /**
         * Wait for Recaptcha to be ready on the window. Once it is ready, get
         * a short-lived token.
         */
        await new Promise((resolve) => {
          window.grecaptcha.ready(() => resolve());
        });
        token = await window.grecaptcha.execute(process.env.CAPTCHA_KEY, {
          action: 'submit',
        });
      }

      const sanitisedValues = sanitiseValues(values, allFields);

      return submit(
        process.env.FORMS_API_URL,
        sanitisedValues,
        fields,
        token,
        setStatus,
        () => onAPISuccess(values),
        onAPIFailure
      );
    },
    [
      onAPIFailure,
      onAPISuccess,
      captcha,
      currentStep,
      totalSteps,
      scrollToTop,
      updateFormDataAndCache,
      allFields,
      fields,
    ]
  );

  /**
   * Formik error method
   */
  const onSubmitError = useCallback(({ values, errors }) => {
    const firstErrorKey = Object.keys(errors)[0];
    const firstError = errors[firstErrorKey];

    // handle nested field names such as address[address1]
    let firstErrorName =
      typeof firstError === 'object'
        ? `${firstErrorKey}[${Object.keys(firstError)[0]}]`
        : firstErrorKey;

    if (firstErrorName.endsWith('[_]')) {
      firstErrorName = firstErrorName.replace('[_]', '[day]');
    }

    const firstErrorRef = fieldRefs.current.find(
      (el) => el?.dataset?.inputScrollId === firstErrorName
    );

    // scrollIntoView() is necessary to move the view in Safari on iOS devices
    firstErrorRef?.scrollIntoView({ block: 'center', behavior: 'instant' });
    firstErrorRef?.focus();

    sendEvent({
      event: 'formValidation',
      formEntryId: values.formEntryId,
      formName: values.formName,
      formHeader: values.formHeader,
      errors,
    });
  }, []);

  const consentText = (values) =>
    consentReplacement ? (
      consentReplacement()
    ) : (
      <Consent options={inferConsentTypes(allFields)} formValues={values} />
    );

  return (
    <ModuleWrapper>
      <Container $padding={!insideContainer}>
        <Formik
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ submitCount, isSubmitting, errors, values }) =>
            !submitted ? (
              <>
                <FormWrapper $backgroundColor={backgroundColor}>
                  <FormikForm
                    noValidate
                    ref={formTop}
                    id={consistentString(adminTitle)}
                    aria-labelledby={
                      formHeader
                        ? `${consistentString(adminTitle)}-label`
                        : undefined
                    }
                  >
                    {(formHeader || isSteppedForm) && (
                      <FormHeaderElement
                        id={`${consistentString(adminTitle)}-label`}
                        ref={formHeaderRef}
                      >
                        {formHeader || ''}
                        {formHeader && isSteppedForm ? ' - ' : ''}
                        {isSteppedForm &&
                          `Step ${currentStep + 1} of ${totalSteps}`}
                      </FormHeaderElement>
                    )}
                    {Object.keys(hiddenFields).map((name) => (
                      <FormikField
                        type="hidden"
                        name={name}
                        key={name}
                        hidden
                      />
                    ))}

                    <SubmitErrorHandler
                      onSubmitError={onSubmitError}
                      reset={submitCount === 0}
                    />
                    {steps[currentStep] &&
                      renderFields(steps[currentStep], addToFieldRefs, values)}

                    {showConsent && isFinalStep ? consentText(values) : null}

                    <FormFieldWrapper>
                      {currentStep > 0 && !isSurvey ? (
                        <PreviousButtonWrapper>
                          <Button
                            bg="secondary-outline"
                            onClick={() => {
                              updateFormDataAndCache(values, -1);
                              scrollToTop();
                            }}
                            disabled={isSubmitting}
                            aria-disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Processing...' : 'Previous'}
                          </Button>
                        </PreviousButtonWrapper>
                      ) : null}
                      {currentStep < totalSteps - 1 ? (
                        <>
                          <Button
                            bg="secondary-standard"
                            type="submit"
                            disabled={isSubmitting}
                            aria-disabled={isSubmitting}
                            id={
                              isSurvey
                                ? `survey-next-btn-step-${currentStep + 1}`
                                : undefined
                            }
                          >
                            {isSubmitting ? 'Processing...' : 'Next'}
                          </Button>
                          <FieldMessage
                            error={
                              Object.keys(errors).length > 0
                                ? 'There was a problem submitting your form. Please correct any errors and try again.'
                                : null
                            }
                          />
                        </>
                      ) : null}
                      {isFinalStep ? (
                        <>
                          <Button
                            bg={submitColor}
                            type="submit"
                            disabled={isSubmitting}
                            aria-disabled={isSubmitting}
                            id={
                              isSurvey
                                ? `survey-next-btn-step-final`
                                : undefined
                            }
                          >
                            {isSubmitting
                              ? 'Submitting...'
                              : submitCallToAction}
                          </Button>
                          <FieldMessage
                            error={
                              Object.keys(errors).length > 0
                                ? 'There was a problem submitting your form. Please correct any errors and try again.'
                                : null
                            }
                            submitCount={submitCount}
                          />
                        </>
                      ) : null}
                    </FormFieldWrapper>
                  </FormikForm>
                </FormWrapper>
                {submitCount > 0 && responseMessage && (
                  <InlineCallout forwardedRef={messageRef}>
                    {responseMessage}
                  </InlineCallout>
                )}
              </>
            ) : (
              <InlineCallout forwardedRef={messageRef} role="alert">
                {thankYouMessage ? (
                  <RichText richText={thankYouMessage} />
                ) : (
                  'Thank you. The form has been submitted.'
                )}
              </InlineCallout>
            )
          }
        </Formik>
      </Container>
    </ModuleWrapper>
  );
};

Form.propTypes = {
  formEntryId: PropTypes.string.isRequired,
  adminTitle: PropTypes.string.isRequired,
  campaignId: PropTypes.string,
  submitCallToAction: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        assembly: PropTypes.bool.isRequired,
        id: PropTypes.string.isRequired,
        internal: PropTypes.shape({
          type: PropTypes.string.isRequired,
        }),
      }),
      PropTypes.shape(FieldsetPropTypes),
      PropTypes.shape(FieldPropTypes),
    ])
  ),
  formHeader: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['white', 'grey']),
  showConsent: PropTypes.bool,
  thankYouMessage: RichTextPropTypes,
  redirectAfterSubmission: PropTypes.arrayOf(PropTypes.object),
  captcha: PropTypes.bool,
  event: PropTypes.object,
  submitColor: PropTypes.string,
  consentReplacement: PropTypes.func,
  sendToCRM: PropTypes.bool,
  isSurvey: PropTypes.bool,
};

Form.defaultProps = {
  captcha: true,
  event: null,
};

export default Form;

export const FormAssembly = ({ contentfulData, event, isSurvey }) => {
  const fields = buildFieldsFromContentfulData(contentfulData.formFields);
  return (
    <Form
      formEntryId={contentfulData.contentful_id}
      adminTitle={contentfulData.administrativeTitle}
      campaignId={contentfulData.campaignId}
      submitCallToAction={contentfulData.submitCallToAction}
      fields={fields || []}
      formHeader={contentfulData.formHeader || undefined}
      backgroundColor={consistentString(
        contentfulData.backgroundColor || 'white'
      )}
      showConsent={contentfulData.showConsent}
      thankYouMessage={contentfulData.thankYouMessage}
      redirectAfterSubmission={contentfulData.redirectAfterSubmission}
      machineName={contentfulData.machineName}
      event={event || null}
      submitColor={consistentString(
        contentfulData.submitColor || 'secondary-standard'
      )}
      consentReplacement={contentfulData.consentReplacement}
      sendToCRM={contentfulData.sendToCRM}
      isSurvey={isSurvey}
    />
  );
};

FormAssembly.propTypes = {
  contentfulData: PropTypes.object,
  event: PropTypes.object,
  isSurvey: PropTypes.bool,
};
